import React, { useCallback, useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  archiveMembershipPlan,
  assignMembershipPlan,
  fetchAdminMembershipPlans,
  fetchAdminPlanAssignments,
  saveMembershipPlan,
  saveMembershipPlanFeatures
} from '../../services/membershipPlanService';
import MembershipPlanEditor from './MembershipPlanEditor';

const { FiPlus, FiEdit2, FiLoader, FiRefreshCw, FiUsers, FiX } = FiIcons;

const AdminMembershipPlansPanel = () => {
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [editingPlan, setEditingPlan] = useState(undefined);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [duration, setDuration] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [planRows, userRows, assignmentRows] = await Promise.all([
        fetchAdminMembershipPlans(),
        import('../../supabase/supabase').then(({ supabase }) =>
          supabase.rpc('admin_get_user_list').then(({ data, error: rpcError }) => {
            if (rpcError) throw rpcError;
            return data || [];
          })
        ),
        fetchAdminPlanAssignments()
      ]);
      setPlans(planRows);
      setUsers(userRows);
      setAssignments(assignmentRows);
      if (!selectedPlan && planRows[0]?.id) setSelectedPlan(planRows[0].id);
    } catch (requestError) {
      setError(requestError.message || 'Unable to load plan management.');
    } finally {
      setLoading(false);
    }
  }, [selectedPlan]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const savePlan = async (plan) => {
    setSaving(true);
    setError('');
    try {
      const { featurePlans, ...planDetails } = plan;
      if (featurePlans?.length) {
        const originalPlans = new Map(plans.map((item) => [item.id, item]));
        const changedPlans = featurePlans.filter((item) => (
          item.id === '__new_plan__'
          || item.id === planDetails.id
          || JSON.stringify(item.features) !== JSON.stringify(originalPlans.get(item.id)?.features || {})
        ));
        const selectedPlan = originalPlans.get(planDetails.id);
        const detailsChanged = !selectedPlan
          || ['name', 'slug', 'description', 'currency', 'is_active', 'is_trial', 'plan_type']
            .some((key) => selectedPlan[key] !== planDetails[key])
          || Number(selectedPlan.monthly_price) !== Number(planDetails.monthly_price)
          || Number(selectedPlan.trial_duration_days) !== Number(planDetails.trial_duration_days)
          || Number(selectedPlan.claim_interval_months) !== Number(planDetails.claim_interval_months);

        await Promise.all(changedPlans.map((item) => (
          item.id === '__new_plan__'
            ? saveMembershipPlan({ ...planDetails, features: item.features })
            : item.id === planDetails.id
              ? detailsChanged
                ? saveMembershipPlan({ ...item, ...planDetails, features: item.features })
                : saveMembershipPlanFeatures(item.id, item.features)
              : saveMembershipPlanFeatures(item.id, item.features)
        )));
      } else {
        await saveMembershipPlan(planDetails);
      }
      setEditingPlan(undefined);
      setNotice('Plan saved. Current seller access uses the updated features and terms.');
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to save this plan.');
    } finally {
      setSaving(false);
    }
  };

  const togglePlanStatus = async (plan) => {
    const nextActive = !plan.is_active;
    if (plan.plan_type === 'free' && !nextActive) return;
    if (!nextActive && !window.confirm(
      `Disable ${plan.name}? Sellers will see it as unavailable, current access will fall back to Free, and active Stripe subscriptions will be scheduled to end. Plan records and assignments are preserved.`
    )) return;

    setSaving(true);
    setError('');
    try {
      await saveMembershipPlan({ ...plan, is_active: nextActive });
      setNotice(nextActive
        ? `${plan.name} is available to sellers again.`
        : `${plan.name} is disabled. Existing plan records and assignments are preserved.`);
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to update plan availability.');
    } finally {
      setSaving(false);
    }
  };

  const archivePlan = async (plan) => {
    if (!window.confirm(`Remove ${plan.name} from future use? Sellers on this plan will move to Free.`)) return;
    setSaving(true);
    setError('');
    try {
      await archiveMembershipPlan(plan.id);
      setNotice('Plan archived. Its sellers move to Free and active Stripe subscriptions are canceled.');
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to archive this plan.');
    } finally {
      setSaving(false);
    }
  };

  const assignPlan = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const selected = plans.find((plan) => plan.id === selectedPlan);
      await assignMembershipPlan(selectedUser, selectedPlan, duration);
      setNotice(`Plan enabled for ${users.find((person) => person.id === selectedUser)?.name || 'the selected user'}.`);
      if (selected?.is_trial) setDuration(1);
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to assign this plan.');
    } finally {
      setSaving(false);
    }
  };

  const activePlans = plans.filter((plan) => plan.is_active);
  const chosenPlan = plans.find((plan) => plan.id === selectedPlan);

  return (
    <section className="mt-8 rounded-[2.5rem] border border-gray-100 bg-white p-6 shadow-sm md:p-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-gray-900">Membership Plans</h2>
          <p className="mt-1 text-xs text-slate-500">Manage monthly access, plan features, trials, and user assignments.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => refresh()} className="rounded-xl bg-slate-100 p-3 text-slate-600" aria-label="Refresh plans">
            <SafeIcon icon={FiRefreshCw} />
          </button>
          <button type="button" onClick={() => setEditingPlan(null)} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-xs font-black uppercase tracking-wider text-white">
            <SafeIcon icon={FiPlus} /> New plan
          </button>
        </div>
      </header>

      {error && <p role="alert" className="mt-4 rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-700">{error}</p>}
      {notice && <p role="status" className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm font-bold text-emerald-700">{notice}</p>}

      {editingPlan !== undefined && (
        <div className="mt-5 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-black text-slate-800">{editingPlan?.id ? 'Edit plan' : 'Create plan'}</h3>
            <button type="button" onClick={() => setEditingPlan(undefined)} aria-label="Close editor"><SafeIcon icon={FiX} /></button>
          </div>
          <MembershipPlanEditor plan={editingPlan} plans={plans} onSave={savePlan} onCancel={() => setEditingPlan(undefined)} saving={saving} />
        </div>
      )}

      {loading ? <SafeIcon icon={FiLoader} className="mt-6 animate-spin text-2xl text-indigo-600" /> : (
        <>
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {plans.map((plan) => (
              <article key={plan.id} className={`rounded-2xl border p-5 ${plan.is_active ? 'border-slate-200' : 'border-slate-100 bg-slate-50 opacity-70'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-black text-slate-900">{plan.name} {!plan.is_active && <span className="text-xs text-slate-400">(Archived)</span>}</h3>
                    <p className="mt-1 text-xs text-slate-500">{plan.description || plan.slug}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                      <input
                        type="checkbox"
                        checked={plan.is_active}
                        disabled={saving || (plan.plan_type === 'free' && plan.is_active)}
                        onChange={() => togglePlanStatus(plan)}
                        aria-label={`${plan.is_active ? 'Disable' : 'Enable'} ${plan.name}`}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                      />
                      {plan.is_active ? 'On' : 'Off'}
                    </label>
                    <button type="button" onClick={() => setEditingPlan(plan)} aria-label={`Edit ${plan.name}`} className="rounded-lg bg-indigo-50 p-2 text-indigo-600"><SafeIcon icon={FiEdit2} /></button>
                  </div>
                </div>
                <p className="mt-3 text-sm font-black text-indigo-700">
                  {plan.plan_type === 'trial'
                    ? `Trial · ${plan.trial_duration_days} days · claim every ${plan.claim_interval_months} months`
                    : plan.plan_type === 'free'
                      ? 'Free default plan'
                      : `${plan.currency.toUpperCase()} ${Number(plan.monthly_price).toFixed(2)} / month`}
                </p>
                <ul className="mt-3 space-y-1 text-xs text-slate-600">
                  {Object.entries(plan.features || {})
                    .filter(([, detail]) => detail && String(detail).toLowerCase() !== 'false')
                    .map(([feature, detail]) => <li key={feature}>• <b>{feature}</b>{detail === true ? '' : ` — ${detail}`}</li>)}
                </ul>
                {plan.is_active && plan.plan_type !== 'free' && <button type="button" disabled={saving} onClick={() => archivePlan(plan)} className="mt-4 text-xs font-black uppercase tracking-wider text-rose-600 disabled:opacity-50">Remove plan</button>}
              </article>
            ))}
          </div>

          <form onSubmit={assignPlan} className="mt-7 rounded-2xl bg-slate-50 p-5">
            <h3 className="flex items-center gap-2 font-black text-slate-900"><SafeIcon icon={FiUsers} /> Manually enable a plan</h3>
            <div className="mt-4 grid gap-3 md:grid-cols-4">
              <select required value={selectedUser} onChange={(event) => setSelectedUser(event.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm">
                <option value="">Choose user</option>
                {users.map((person) => <option key={person.id} value={person.id}>{person.name} — {person.email}</option>)}
              </select>
              <select required value={selectedPlan} onChange={(event) => setSelectedPlan(event.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm">
                <option value="">Choose plan</option>
                {activePlans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
              </select>
              <input type="number" min="1" max="120" value={duration} disabled={chosenPlan?.is_trial} onChange={(event) => setDuration(event.target.value)} aria-label="Assignment duration in months" className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm disabled:bg-slate-100" />
              <button disabled={saving || !selectedUser || !selectedPlan} className="rounded-xl bg-slate-900 px-4 py-3 text-xs font-black uppercase tracking-wider text-white disabled:opacity-50">{saving ? 'Saving...' : 'Enable plan'}</button>
            </div>
            <p className="mt-2 text-xs text-slate-500">{chosenPlan?.is_trial ? `This trial assignment expires after ${chosenPlan.trial_duration_days} days.` : 'Monthly plan access is enabled for the selected number of months.'}</p>
          </form>

          <div className="mt-7">
            <h3 className="font-black text-slate-900">Recent user plan access</h3>
            {assignments.length === 0 ? <p className="mt-2 text-sm text-slate-500">No assignments yet.</p> : (
              <div className="mt-3 divide-y divide-slate-100">
                {assignments.map((assignment) => (
                  <div key={assignment.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                    <div><p className="font-bold text-slate-800">{assignment.user_name} · {assignment.plan_name}</p><p className="text-xs text-slate-500">{assignment.user_email} · {assignment.source === 'trial' ? 'Trial claim' : 'Admin assigned'}</p></div>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${assignment.is_current ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{assignment.is_current ? 'Active' : `Expired ${new Date(assignment.expires_at).toLocaleDateString()}`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default AdminMembershipPlansPanel;
